<template>
  <div class="phone-input">
    <input
      ref="inputRef"
      :id="id"
      :name="name"
      :type="type"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      :class="inputClasses"
      :autocomplete="autocomplete"
      pattern="[0-9+\x20\-]+"
      inputmode="numeric"
      @input="handleInput"
      @blur="handleBlur"
      @countrychange="handleCountryChange"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import 'intl-tel-input/build/css/intlTelInput.css'

interface Props {
  modelValue?: string
  label?: string
  placeholder?: string
  id?: string
  name?: string
  type?: string
  required?: boolean
  disabled?: boolean
  error?: string
  autocomplete?: string
  initialCountry?: string
  preferredCountries?: string[]
  separateDialCode?: boolean
  classNames?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  label: '',
  placeholder: 'Enter phone number',
  id: '',
  name: '',
  type: 'tel',
  required: false,
  disabled: false,
  error: '',
  autocomplete: 'tel',
  initialCountry: 'DE', // or auto
  preferredCountries: () => ['DE', 'AT', 'CH'],
  separateDialCode: false,
  classNames: 'appearance-none relative block w-full px-3 py-3 border placeholder-transparent text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-base peer'
})

const emit = defineEmits<{
  'blur': [value: string]
  'update:modelValue': [value: string]
}>()

const inputRef = ref<HTMLInputElement>()
let iti: any = null

// Computed classes
const inputClasses = computed(() => [
  props.classNames,
  props.error
    ? 'border-red focus:border-red'
    : 'border-secondary-300',
  props.disabled ? 'opacity-50 cursor-not-allowed' : ''
])

// Initialize intl-tel-input
const initializeIntlTelInput = async () => {
  if (!inputRef.value) return

  const intlTelInput = (await import('intl-tel-input')).default
  
  iti = intlTelInput(inputRef.value, {
    initialCountry: props.initialCountry,
    preferredCountries: props.preferredCountries,
    separateDialCode: false,
    geoIpLookup: async (callback: (countryCode: string) => void) => {
      try {
        const res = await fetch('https://ipapi.co/json/')
        const data = await res.json()
        callback(data.country_code)
      } catch {
        callback('DE')
      }
    },
    utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@20.1.5/build/js/utils.js'
  })

  // Set initial value if provided
  if (props.modelValue) {
    inputRef.value.value = props.modelValue;
    handleBlur()
  }
}

// Handle input changes
const handleInput = () => {
  if (!inputRef.value || !iti) return

  inputRef.value.value = inputRef.value.value.replace(/[^0-9+\s-]/g, "");

  emitCountryChanged()
}

// Handle blur event
const handleBlur = () => {
  if (!inputRef.value || !iti) return
  const number = iti.getNumber()
  emit('blur', number || inputRef.value.value)
  emitCountryChanged()

}

// Handle country change
const handleCountryChange = () => {
  if (!iti) return

  emitCountryChanged()
  // Update the number with new country code
  handleInput()
}

function emitCountryChanged() {
  if (!inputRef.value || !iti) return

  const countryData = iti.getSelectedCountryData()
  const inputValue = inputRef.value.value.trim()
  
  // Check if the input already has a country code (starts with +)
  let number = ''
  if (inputValue.length  === 0) {
    emit('update:modelValue', inputValue)
    return
  }
  if (inputValue.startsWith('+')) {
    // Input already has country code, use as is
    number = inputValue
  } else {
    // Input doesn't have country code, append it
    number = "+" + countryData?.dialCode + " " + inputValue
  }
  inputRef.value.value = number
  
  // Emit update:modelValue to update form data
  emit('update:modelValue', number)
}

// Lifecycle
onMounted(async () => {
  await initializeIntlTelInput()
})

onUnmounted(() => {
  if (iti) {
    iti.destroy()
  }
})

</script>